import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import MapGL, { Marker, Popup, ViewState, Source, Layer } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { MapRef } from 'react-map-gl';
import {
  MapPin, ExternalLink, Ticket, Clock, Tag, TreePine, ChevronLeft, Calendar,
  Music, Palette, Utensils, Dumbbell, Users, Layers, ChevronDown, Sparkles,
} from 'lucide-react';
import { format } from 'date-fns';
import type { Event } from '../types/event';
import MapControls from './MapControls';

function isSafeUrl(url: string | null | undefined): url is string {
  if (!url) return false;
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

// ─── Category system ─────────────────────────────────────────────────────────

const CATEGORIES = {
  music:    { color: '#f43f5e', label: 'Music',       Icon: Music    },
  arts:     { color: '#14b8a6', label: 'Arts',        Icon: Palette  },
  food:     { color: '#f97316', label: 'Food & Drink',Icon: Utensils },
  sports:   { color: '#0ea5e9', label: 'Sports',      Icon: Dumbbell },
  outdoors: { color: '#22c55e', label: 'Outdoors',    Icon: TreePine },
  family:   { color: '#f59e0b', label: 'Family',      Icon: Users    },
  default:  { color: '#f97316', label: 'Other',       Icon: Sparkles },
} as const;

type CategoryKey = keyof typeof CATEGORIES;

function getCategoryKey(categories: string[] | null): CategoryKey {
  if (!categories?.length) return 'default';
  const s = categories.join(' ').toLowerCase();
  if (/music|concert|band|jazz|rock|hip.?hop|dj|live.*music/.test(s)) return 'music';
  if (/art|theater|theatre|gallery|exhibit|museum|dance|film|comedy|perform/.test(s)) return 'arts';
  if (/food|drink|beer|wine|culinary|dining|tast|cuisine/.test(s)) return 'food';
  if (/sport|fitness|run|race|yoga|gym|game|match|tournament|marathon/.test(s)) return 'sports';
  if (/outdoor|hike|nature|trail|park|garden|camping|kayak/.test(s)) return 'outdoors';
  if (/family|kid|child|baby|parent/.test(s)) return 'family';
  return 'default';
}

// ─── Circle GeoJSON helper ────────────────────────────────────────────────────

function buildCircleGeoJSON(lat: number, lng: number, radiusM: number) {
  const pts = 72;
  const coords: [number, number][] = [];
  for (let i = 0; i <= pts; i++) {
    const a = (i / pts) * 2 * Math.PI;
    const dLng = (radiusM * Math.cos(a)) / (111320 * Math.cos((lat * Math.PI) / 180));
    const dLat = (radiusM * Math.sin(a)) / 110540;
    coords.push([lng + dLng, lat + dLat]);
  }
  return {
    type: 'FeatureCollection' as const,
    features: [{
      type: 'Feature' as const,
      geometry: { type: 'Polygon' as const, coordinates: [coords] },
      properties: {},
    }],
  };
}

const RADIUS_OPTIONS = [
  { label: '6 mi', value: 10000 },
  { label: '15 mi', value: 25000 },
  { label: '30 mi', value: 50000 },
  { label: '60 mi', value: 100000 },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function RadiusSelector({ radius, onChange }: { radius: number; onChange: (r: number) => void }) {
  return (
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center bg-white/90 backdrop-blur-md rounded-xl shadow-glass-lg border border-slate-200/80 p-0.5 gap-0.5">
      {RADIUS_OPTIONS.map(({ label, value }) => (
        <button
          key={value}
          onClick={() => onChange(value)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
            radius === value
              ? 'bg-blue-500 text-white shadow-sm shadow-blue-500/30'
              : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function CategoryLegend({ events }: { events: Event[] }) {
  const [open, setOpen] = useState(true);

  const present = useMemo(() => {
    const counts = new Map<CategoryKey, number>();
    for (const e of events) {
      const k = getCategoryKey(e.categories);
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([key, count]) => ({ key, count, ...CATEGORIES[key] }));
  }, [events]);

  if (present.length === 0) return null;

  return (
    <div className="absolute bottom-8 left-4 z-10">
      <div className="bg-white/90 backdrop-blur-md rounded-xl shadow-glass-lg border border-slate-200/80 overflow-hidden">
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:text-slate-900 w-full transition-colors"
        >
          <Layers className="w-3.5 h-3.5 text-slate-400" />
          <span className="hidden sm:inline">Legend</span>
          <ChevronDown className={`w-3 h-3 text-slate-400 ml-auto transition-transform ${open ? '' : '-rotate-90'}`} />
        </button>
        {open && (
          <div className="px-3 pb-2.5 flex flex-col gap-1.5 border-t border-slate-100 pt-2">
            {present.map(({ key, count, color, label, Icon }) => (
              <div key={key} className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color }}>
                  <Icon className="w-2.5 h-2.5 text-white" />
                </div>
                <span className="text-xs text-slate-600 leading-none">{label}</span>
                <span className="text-[10px] font-medium text-slate-400 ml-auto tabular-nums">{count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Shared types ─────────────────────────────────────────────────────────────

interface LocalPark {
  id: string;
  name: string;
  lat: number;
  lng: number;
}

interface EventGroup {
  key: string;
  lat: number;
  lng: number;
  events: Event[];
  hasNationalPark: boolean;
  categoryKey: CategoryKey;
}

interface EventMapProps {
  events: Event[];
  viewState: { longitude: number; latitude: number; zoom: number };
  onViewStateChange: (vs: { longitude: number; latitude: number; zoom: number }) => void;
  selectedEventId?: string | null;
  onSelectEvent: (id: string | null) => void;
  fetchLat: number;
  fetchLng: number;
  radiusMeters: number;
  onRadiusChange: (r: number) => void;
}

// ─── EventDetail popup ────────────────────────────────────────────────────────

function EventDetail({ event, onBack, backLabel }: { event: Event; onBack?: () => void; backLabel?: string }) {
  return (
    <div className="animate-fade-in-up min-w-[17rem] max-w-[24rem] w-max">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 px-3.5 pt-3 pb-2.5 border-b border-slate-100 w-full transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {backLabel ?? 'Back'}
        </button>
      )}
      {event.image_url && (
        <img
          src={event.image_url}
          alt=""
          className="w-full h-36 object-cover"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      )}
      <div className="p-3.5">
        <h3 className="font-semibold text-slate-900 text-sm leading-snug">{event.title}</h3>

        {event.venue_name && (
          <div className="flex items-start gap-1 mt-1.5">
            <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-500 break-words">{event.venue_name}</p>
          </div>
        )}
        {(event.city || event.state) && (
          <p className="text-[11px] text-slate-400 ml-4">
            {[event.city, event.state].filter(Boolean).join(', ')}
          </p>
        )}

        {event.start_datetime && (
          <div className="flex items-center gap-1 mt-1.5">
            <Clock className="w-3 h-3 text-slate-400 flex-shrink-0" />
            <p className="text-xs text-slate-500">
              {format(new Date(event.start_datetime), 'MMM d, yyyy · h:mm a')}
            </p>
          </div>
        )}

        {event.description && (
          <p className="text-xs text-slate-500 mt-2 leading-relaxed max-h-40 overflow-y-auto pr-0.5">
            {event.description}
          </p>
        )}

        <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
          {event.price && (
            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">
              {event.price}
            </span>
          )}
          {event.categories?.slice(0, 3).map((cat, i) => (
            <span key={i} className="flex items-center gap-0.5 text-[10px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
              <Tag className="w-2.5 h-2.5" />
              {cat}
            </span>
          ))}
        </div>

        {(event.url || event.ticket_url) && (
          <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
            {isSafeUrl(event.url) && (
              <a
                href={event.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Details
              </a>
            )}
            {isSafeUrl(event.ticket_url) && event.ticket_url !== event.url && (
              <a
                href={event.ticket_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
              >
                <Ticket className="w-3 h-3" />
                Tickets
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const isNationalPark = (e: Event) =>
  (e.categories ?? []).some(c => c.toLowerCase().includes('national park'));

const isValidCoordinate = (lat: number, lng: number) =>
  Number.isFinite(lat) && Number.isFinite(lng) &&
  lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

const PAN_DISTANCE = 0.1;
const ZOOM_INCREMENT = 0.5;
const ANIMATION_DURATION = 500;

export default function EventMap({
  events, viewState, onViewStateChange, selectedEventId, onSelectEvent,
  fetchLat, fetchLng, radiusMeters, onRadiusChange,
}: EventMapProps) {
  const mapRef = useRef<MapRef>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [localParks, setLocalParks] = useState<LocalPark[]>([]);
  const [selectedPark, setSelectedPark] = useState<LocalPark | null>(null);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  const [groupExpandedId, setGroupExpandedId] = useState<number | null>(null);


  const circleGeoJSON = useMemo(
    () => buildCircleGeoJSON(fetchLat, fetchLng, radiusMeters),
    [fetchLat, fetchLng, radiusMeters],
  );

  const eventGroups = useMemo((): EventGroup[] => {
    const map = new Map<string, Event[]>();
    for (const e of events) {
      if (!isValidCoordinate(e.latitude, e.longitude)) continue;
      const key = `${e.latitude.toFixed(4)},${e.longitude.toFixed(4)}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    }
    return Array.from(map.entries()).map(([key, evs]) => ({
      key,
      lat: evs[0].latitude,
      lng: evs[0].longitude,
      events: evs,
      hasNationalPark: evs.some(isNationalPark),
      categoryKey: getCategoryKey(evs[0].categories),
    }));
  }, [events]);

  const selectedGroup = useMemo(
    () => eventGroups.find(g => g.key === selectedGroupKey) ?? null,
    [eventGroups, selectedGroupKey],
  );

  const groupExpandedEvent = useMemo(
    () => groupExpandedId != null ? (selectedGroup?.events.find(e => e.id === groupExpandedId) ?? null) : null,
    [selectedGroup, groupExpandedId],
  );

  const selectedEvent = useMemo(
    () => selectedEventId != null ? (events.find(e => e.id === selectedEventId) ?? null) : null,
    [events, selectedEventId],
  );

  useEffect(() => {
    if (selectedEventId != null) {
      setSelectedGroupKey(null);
      setGroupExpandedId(null);
      setSelectedPark(null);
    }
  }, [selectedEventId]);

  const queryLocalParks = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    try {
      const layers = map.getStyle()?.layers?.map(l => l.id) ?? [];
      const candidates = ['park-label', 'poi-label', 'natural-label'].filter(l => layers.includes(l));
      if (candidates.length === 0) return;
      const features = map.queryRenderedFeatures(undefined, { layers: candidates });
      const seen = new Set<string>();
      const parks: LocalPark[] = [];
      for (const f of features) {
        const name: string = f.properties?.name || f.properties?.name_en || '';
        if (!name) continue;
        const maki: string = f.properties?.maki ?? '';
        const cls: string = f.properties?.class ?? '';
        const isPark =
          maki === 'park' || cls === 'park' || cls === 'national_park' || cls === 'state_park' ||
          /park|garden|reserve|nature/.test(name.toLowerCase());
        if (!isPark) continue;
        const key = name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        const geom = f.geometry as GeoJSON.Geometry;
        let lat: number | null = null, lng: number | null = null;
        if (geom.type === 'Point') {
          [lng, lat] = (geom as GeoJSON.Point).coordinates;
        } else if (geom.type === 'Polygon') {
          const coords = (geom as GeoJSON.Polygon).coordinates[0];
          lng = coords.reduce((s, c) => s + c[0], 0) / coords.length;
          lat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
        }
        if (lat == null || lng == null) continue;
        parks.push({ id: `lp-${key}`, name, lat, lng });
        if (parks.length >= 80) break;
      }
      setLocalParks(parks);
    } catch { /* skip at low zoom */ }
  }, []);

  const safeViewState = {
    longitude: Number.isFinite(viewState.longitude) && Math.abs(viewState.longitude) <= 180 ? viewState.longitude : 0,
    latitude: Number.isFinite(viewState.latitude) && Math.abs(viewState.latitude) <= 90 ? viewState.latitude : 0,
    zoom: Number.isFinite(viewState.zoom) && viewState.zoom >= 0 ? Math.min(Math.max(viewState.zoom, 1), 20) : 1,
  };

  const handleMove = useCallback((evt: { viewState: ViewState }) => {
    try {
      if (!evt?.viewState) return;
      const { longitude, latitude, zoom } = evt.viewState;
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return;
      if (!isValidCoordinate(latitude, longitude)) return;
      if (!Number.isFinite(zoom) || zoom < 0) return;
      onViewStateChange({
        longitude: Math.max(-180, Math.min(180, longitude)),
        latitude: Math.max(-90, Math.min(90, latitude)),
        zoom: Math.max(1, Math.min(20, zoom)),
      });
    } catch (err) { console.error('handleMove error:', err); }
  }, [onViewStateChange]);

  const clearAll = useCallback(() => {
    onSelectEvent(null);
    setSelectedGroupKey(null);
    setGroupExpandedId(null);
    setSelectedPark(null);
  }, [onSelectEvent]);

  const handleZoomIn  = useCallback(() => onViewStateChange({ ...viewState, zoom: Math.min(viewState.zoom + ZOOM_INCREMENT, 20) }), [viewState, onViewStateChange]);
  const handleZoomOut = useCallback(() => onViewStateChange({ ...viewState, zoom: Math.max(viewState.zoom - ZOOM_INCREMENT, 1) }), [viewState, onViewStateChange]);

  const handlePan = useCallback((dir: 'up' | 'down' | 'left' | 'right') => {
    const nv = { ...viewState, transitionDuration: ANIMATION_DURATION };
    if (dir === 'up')    nv.latitude  = Math.min(nv.latitude + PAN_DISTANCE, 90);
    if (dir === 'down')  nv.latitude  = Math.max(nv.latitude - PAN_DISTANCE, -90);
    if (dir === 'left')  nv.longitude = Math.max(nv.longitude - PAN_DISTANCE, -180);
    if (dir === 'right') nv.longitude = Math.min(nv.longitude + PAN_DISTANCE, 180);
    onViewStateChange(nv);
  }, [viewState, onViewStateChange]);

  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      const actions: Record<string, () => void> = {
        ArrowUp: () => handlePan('up'), ArrowDown: () => handlePan('down'),
        ArrowLeft: () => handlePan('left'), ArrowRight: () => handlePan('right'),
        '+': handleZoomIn, '-': handleZoomOut,
      };
      actions[e.key]?.();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [handlePan, handleZoomIn, handleZoomOut]);

  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN;

  if (!mapboxToken || !mapboxToken.startsWith('pk.eyJ')) {
    return (
      <div className="w-full h-full bg-slate-50 flex items-center justify-center">
        <div className="text-center px-6">
          <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-3">
            <MapPin className="w-6 h-6 text-red-400" />
          </div>
          <p className="text-red-600 text-sm font-medium">Invalid Mapbox token</p>
          <p className="text-slate-400 text-xs mt-1">Token should start with 'pk.eyJ'</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full map-container">
      {!mapLoaded && (
        <div className="absolute inset-0 bg-slate-50 flex items-center justify-center z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-blue-500 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Loading map...</p>
          </div>
        </div>
      )}

      <MapGL
        ref={mapRef}
        {...safeViewState}
        mapboxAccessToken={mapboxToken}
        onMove={handleMove}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/outdoors-v12"
        minZoom={1}
        maxZoom={20}
        onClick={clearAll}
        onLoad={() => { setMapLoaded(true); queryLocalParks(); }}
        onIdle={queryLocalParks}
      >
        {/* Search radius circle */}
        {mapLoaded && (
          <Source id="search-radius" type="geojson" data={circleGeoJSON}>
            <Layer
              id="search-radius-fill"
              type="fill"
              paint={{ 'fill-color': '#3b82f6', 'fill-opacity': 0.05 }}
            />
            <Layer
              id="search-radius-stroke"
              type="line"
              paint={{
                'line-color': '#3b82f6',
                'line-width': 1.5,
                'line-opacity': 0.35,
                'line-dasharray': [5, 3],
              }}
            />
          </Source>
        )}

        {/* Event markers */}
        {eventGroups.map((group) => {
          const count = group.events.length;
          const isGroupSelected = group.key === selectedGroupKey;
          const isSingleSelected = count === 1 && group.events[0].id === selectedEventId;
          const isHighlighted = isGroupSelected || isSingleSelected;
          const isPark = group.hasNationalPark;
          const cat = isPark ? null : CATEGORIES[group.categoryKey];
          const markerColor = isPark ? '#10b981' : cat!.color;
          const MarkerIcon = isPark ? TreePine : cat!.Icon;

          return (
            <Marker
              key={group.key}
              longitude={group.lng}
              latitude={group.lat}
              anchor="center"
              onClick={(e) => {
                e.originalEvent?.stopPropagation();
                setSelectedPark(null);
                if (count === 1) {
                  onSelectEvent(group.events[0].id);
                  setSelectedGroupKey(null);
                } else {
                  setSelectedGroupKey(isGroupSelected ? null : group.key);
                  setGroupExpandedId(null);
                  onSelectEvent(null);
                }
              }}
            >
              <div className="relative cursor-pointer group">
                <div
                  className={`flex items-center justify-center rounded-full border-2 border-white shadow-lg transition-all duration-200 ${
                    isHighlighted ? 'w-11 h-11 shadow-xl scale-110' : 'w-8 h-8 group-hover:scale-110'
                  }`}
                  style={{ backgroundColor: markerColor, boxShadow: `0 4px 12px ${markerColor}50` }}
                >
                  <MarkerIcon className={`text-white ${isHighlighted ? 'w-5 h-5' : 'w-3.5 h-3.5'}`} />
                </div>
                {count > 1 && (
                  <div
                    className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 rounded-full flex items-center justify-center border-2 border-white shadow-sm"
                    style={{ backgroundColor: markerColor }}
                  >
                    <span className="text-[10px] font-bold text-white leading-none px-1">{count}</span>
                  </div>
                )}
                {isHighlighted && (
                  <div
                    className="absolute inset-0 rounded-full opacity-25 animate-ping"
                    style={{ backgroundColor: markerColor }}
                  />
                )}
              </div>
            </Marker>
          );
        })}

        {/* Single-event popup */}
        {selectedEvent && !selectedGroupKey && isValidCoordinate(selectedEvent.latitude, selectedEvent.longitude) && (
          <Popup
            longitude={selectedEvent.longitude}
            latitude={selectedEvent.latitude}
            anchor="bottom"
            onClose={() => onSelectEvent(null)}
            closeOnClick={false}
            offset={20}
          >
            <EventDetail event={selectedEvent} />
          </Popup>
        )}

        {/* Multi-event group popup — list */}
        {selectedGroup && !groupExpandedEvent && (
          <Popup
            longitude={selectedGroup.lng}
            latitude={selectedGroup.lat}
            anchor="bottom"
            onClose={() => { setSelectedGroupKey(null); setGroupExpandedId(null); }}
            closeOnClick={false}
            offset={20}
          >
            <div className="animate-fade-in-up min-w-[17rem] max-w-[24rem] w-max">
              <div className="flex items-center gap-2 px-3.5 pt-3 pb-2.5 border-b border-slate-100">
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-3.5 h-3.5 text-blue-600" />
                </div>
                <span className="text-sm font-semibold text-slate-800">
                  {selectedGroup.events.length} events at this location
                </span>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-50">
                {selectedGroup.events.map((event) => (
                  <button
                    key={event.id}
                    onClick={() => setGroupExpandedId(event.id)}
                    className="w-full text-left flex items-start gap-2.5 px-3 py-2.5 hover:bg-slate-50 transition-colors"
                  >
                    {event.image_url
                      ? <img src={event.image_url} alt="" className="w-10 h-10 rounded-md object-cover flex-shrink-0 mt-0.5"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      : <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Calendar className="w-4 h-4 text-slate-300" />
                        </div>
                    }
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-800 line-clamp-1 leading-snug">{event.title}</p>
                      {event.start_datetime && (
                        <p className="text-[11px] text-slate-400 mt-0.5">{format(new Date(event.start_datetime), 'MMM d · h:mm a')}</p>
                      )}
                      {event.venue_name && <p className="text-[11px] text-slate-400 truncate">{event.venue_name}</p>}
                    </div>
                    {event.price && (
                      <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded self-start flex-shrink-0 mt-0.5">
                        {event.price}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </Popup>
        )}

        {/* Multi-event group popup — expanded */}
        {selectedGroup && groupExpandedEvent && (
          <Popup
            longitude={selectedGroup.lng}
            latitude={selectedGroup.lat}
            anchor="bottom"
            onClose={() => { setSelectedGroupKey(null); setGroupExpandedId(null); }}
            closeOnClick={false}
            offset={20}
          >
            <EventDetail
              event={groupExpandedEvent}
              onBack={() => setGroupExpandedId(null)}
              backLabel={`${selectedGroup.events.length} events here`}
            />
          </Popup>
        )}

        {/* Local park markers */}
        {localParks.map((park) => (
          <Marker
            key={park.id}
            longitude={park.lng}
            latitude={park.lat}
            anchor="center"
            onClick={(e) => {
              e.originalEvent?.stopPropagation();
              setSelectedPark(p => p?.id === park.id ? null : park);
              onSelectEvent(null);
              setSelectedGroupKey(null);
              setGroupExpandedId(null);
            }}
          >
            <div className="relative cursor-pointer group">
              <div className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-white bg-teal-500 shadow-md shadow-teal-500/30 transition-all duration-200 group-hover:scale-110">
                <TreePine className="w-3.5 h-3.5 text-white" />
              </div>
            </div>
          </Marker>
        ))}

        {selectedPark && (
          <Popup
            longitude={selectedPark.lng}
            latitude={selectedPark.lat}
            anchor="bottom"
            onClose={() => setSelectedPark(null)}
            closeOnClick={false}
            offset={18}
          >
            <div className="px-3 py-2.5 min-w-[160px]">
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <TreePine className="w-3 h-3 text-teal-600" />
                </div>
                <span className="text-[10px] font-semibold text-teal-600 uppercase tracking-wide">Local Park</span>
              </div>
              <p className="font-semibold text-slate-900 text-sm leading-snug">{selectedPark.name}</p>
            </div>
          </Popup>
        )}

        <MapControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} onPan={handlePan} />
      </MapGL>

      {/* Overlays outside MapGL so they're always on top */}
      <RadiusSelector radius={radiusMeters} onChange={onRadiusChange} />
      <CategoryLegend events={events} />
    </div>
  );
}
